import type { ReactNode } from 'react'

/**
 * A line of text above a form or a table: success, warning, or failure.
 *
 * **There is no toast in this application** (§5.3). A message that disappears after four
 * seconds is the wrong medium for "you have just revoked a key", so a notice stays until
 * the page changes.
 *
 * It announces politely by default. A destructive result announces assertively (§21),
 * which is what `assertive` is for — a notice the owner did not ask for, about something
 * that has already happened.
 *
 * **A notice may lead with a tag** — one short word or phrase ("Do not retry") in front of
 * the sentence, drawn as a small-caps label. The tag is part of the message, not a second
 * channel: it is read by a screen reader in its place, so it must still make sense alone.
 * Without one the markup is exactly what it always was.
 */
export type NoticeTone = 'failure' | 'warning' | 'success' | 'info'

export type NoticeProps = {
  readonly tone: NoticeTone
  readonly children: ReactNode
  readonly assertive?: boolean
  /** So a field can point at it with `aria-describedby`. */
  readonly id?: string
  /** A leading label, drawn apart from the sentence. */
  readonly tag?: string
}

export function Notice({ tone, children, assertive = false, id, tag }: NoticeProps) {
  return (
    <p
      className={
        tag === undefined ? `notice notice-${tone}` : `notice notice-${tone} notice-tagged`
      }
      // Nothing is conveyed by colour alone (§21): the tone is a word in the text, and
      // this is only how it is drawn.
      role={assertive ? 'alert' : 'status'}
      {...(id === undefined ? {} : { id })}
    >
      {tag === undefined ? (
        children
      ) : (
        <>
          <span className="notice-tag">{tag}</span>
          <span>{children}</span>
        </>
      )}
    </p>
  )
}
